/**
 * PATH: packages/email-hub/src/feature/components/filter-sidebar/JobSection.tsx
 * FILE_ID: EMAILHUB-FILTERSIDEBAR-JOB-001
 * PURPOSE: Renders searchable job facet filters.
 */
import { useMemo, useState } from 'react'
import { Briefcase, Search, X } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import type { EmailFacets } from '../../hooks/useEmails'
import { jobColor } from '../../lib/colorPalettes'
import { SectionHeader } from './SidebarChrome'
import type { SectionProps } from './types'

interface JobSectionProps extends SectionProps {
  facets: EmailFacets | null
  selectedIds: number[]
  onChange: (ids: number[]) => void
}

export function JobSection({
  expanded,
  onToggle,
  facets,
  selectedIds,
  onChange,
}: JobSectionProps) {
  const [search, setSearch] = useState('')
  const filtered = useMemo(() => {
    const jobs = facets?.jobs || []
    const q = search.trim().toLowerCase()
    if (!q) return jobs
    return jobs.filter((j) => String(j.job_id).includes(q))
  }, [search, facets?.jobs])

  const toggleId = (id: number) => {
    const set = new Set(selectedIds)
    if (set.has(id)) set.delete(id)
    else set.add(id)
    onChange(Array.from(set))
  }

  return (
    <div className="mb-3">
      <SectionHeader
        label="Job"
        expanded={expanded}
        onToggle={onToggle}
        count={selectedIds.length}
      />
      {expanded && (
        <div className="space-y-0.5">
          <div className="relative mb-1">
            <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <input
              type="text"
              placeholder="Find job…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-xs pl-6 pr-2 py-1 border border-border rounded bg-background text-foreground"
            />
          </div>
          {filtered.length === 0 ? (
            <p className="text-[10px] text-muted-foreground px-2 italic">
              No matching jobs.
            </p>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              {filtered.slice(0, 30).map((job) => {
                const c = jobColor(job.job_id)
                const active = selectedIds.includes(job.job_id)
                return (
                  <button
                    key={job.job_id}
                    onClick={() => toggleId(job.job_id)}
                    className={cn(
                      'w-full text-left px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5',
                      active
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-accent',
                    )}
                  >
                    <span className={cn('h-2 w-2 rounded-full shrink-0', c.dot)} />
                    <Briefcase className="h-3 w-3 shrink-0" />
                    <span className="flex-1 truncate">#{job.job_id}</span>
                    <span className="text-[9px] tabular-nums opacity-70">{job.count}</span>
                  </button>
                )
              })}
            </div>
          )}
          {selectedIds.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="w-full text-left px-2 py-1 text-[10px] text-muted-foreground hover:underline"
            >
              <X className="h-2.5 w-2.5 inline" /> Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  )
}
